import React, { useCallback, useState } from 'react'
import {
  toMajorUnits,
  trackAddToCart,
  trackSelectPromotion,
} from '@src/analytics/trackingService'
import { useTranslation } from 'react-i18next'

import { AlertModal } from '@src/components/Modal/AlertModal'
import { ButtonType } from '@src/components/Button'

import { ItemCounter } from '@src/components/ItemCounter/ItemCounter'
import { SpicyChiliSVG } from '@src/components/SVGS/SpicyChiliSVG'
import { AllergenTags } from '@src/components/AllergenTags/AllergenTags'
import {
  ItemDetailsHeader,
  MenuDescription,
} from '@src/pages/OutletPage/OutletMenu/MenuItem/MenuItem.styles'
import {
  AddOnDetailsColumn,
  AddOnItemContainer,
  AddOnItemDetails,
  AddOnItemPrice,
  AddOnItemRegularPrice,
  AddOnPriceContainer,
  AllergenTagHolder,
  ChilliContainer,
  DetailsContainer,
  HeaderHolder,
  ImageContainer,
  ItemImage,
  StyledBasketBoostSVG,
} from './AddOnItems.styles'

import { BasketItem } from '@src/hooks/useBasketItems/validation'
import { useBasketItems } from '@src/hooks/useBasketItems/useBasketItems'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { useBasketOutletId } from '@src/hooks/useBasketOutletId'
import { imageJitURL } from '@src/utils/imageJitURL'
import { Allergen } from '@src/graphql-types'
import { AddOnMenuGroups } from './queries/__generated__/AddOnMenuGroups'
import { useTheme } from 'styled-components'

type AddOnItemsProps = {
  addOnItem: AddOnMenuGroups['menuItemGroupsForOutlet'][0]['outletMenuItems'][0]
  groupIsAtMaxQuantity: boolean
  showItemThumbnail: boolean
  conflictingGroup: string | null // Only relevant when business has highest qualifying add on menu enabled
}

export const AddOnItemCard = ({
  addOnItem,
  groupIsAtMaxQuantity,
  showItemThumbnail,
  conflictingGroup,
}: AddOnItemsProps) => {
  const { addItem, removeItem, removeItems, updateQuantity, addOnItems } =
    useBasketItems()
  const { basketOutletId } = useBasketOutletId()
  const marketplace = useMarketplace()
  const formatCurrency = useFormatCurrency()
  const { t } = useTranslation('addOnItems')
  const theme = useTheme()
  const [showConflictModal, setShowConflictModal] = useState(false)

  const {
    name,
    id: outletMenuItemId,
    menuItemId,
    price,
    addOnMaxPrice,
    minimumPurchasePrice,
    isDairyFree,
    isGlutenFree,
    isVegan,
    isVegetarian,
    isKeto,
    ageRestricted,
    description,
    image,
    maxPurchaseQuantity,
    parentMenu: { id: parentMenuId, name: parentMenuName },
  } = addOnItem
  const discountPrice = minimumPurchasePrice || 0

  const quantityOfSelectedItem =
    addOnItems.find(item => item.menuItemId === menuItemId)?.quantity || 0

  const selectAddOnItem = useCallback(
    (item: Omit<BasketItem, 'id' | 'availability'>) => {
      const addItemAction = () => {
        addItem({
          item,
          parentMenuId,
          parentMenuName,
          outletId: basketOutletId || '',
        })
        trackAddToCart({
          value: toMajorUnits(discountPrice),
          currency: marketplace.country.currency.iso4217,
          affiliation: 'AddOnItems',
          items: [
            {
              itemId: item.menuItemId,
              itemName: item.name,
              price: toMajorUnits(discountPrice),
            },
          ],
        })
        trackSelectPromotion({
          promotionName: 'Add-on Items',
          affiliation: 'AddOnItems',
          items: [
            {
              itemId: item.menuItemId,
              itemName: item.name,
              price: toMajorUnits(discountPrice),
            },
          ],
        })
      }

      if (conflictingGroup) {
        setShowConflictModal(true)
      } else {
        addItemAction()
      }
    },
    [
      addItem,
      basketOutletId,
      conflictingGroup,
      parentMenuId,
      parentMenuName,
      discountPrice,
    ]
  )

  const handleConflictConfirm = useCallback(() => {
    // Remove all existing add-on items
    const addOnItemsToRemove = addOnItems.map(({ id }) => id)

    // Add the new item
    addItem({
      item: {
        menuItemId,
        price: discountPrice,
        quantity: 1,
        outletMenuItemId,
        name,
        optionItemIds: [],
        singleItemNotes: '',
        addOnItem: true,
      },
      parentMenuId,
      parentMenuName,
      outletId: basketOutletId || '',
    })

    removeItems({
      basketItemIds: addOnItemsToRemove,
      shouldNavigateToBasket: false,
    })

    setShowConflictModal(false)
  }, [
    addOnItems,
    addItem,
    removeItems,
    menuItemId,
    outletMenuItemId,
    name,
    basketOutletId,
    parentMenuId,
    parentMenuName,
    discountPrice,
  ])

  const presentAllergens = () => {
    const allergensArr = []
    if (isDairyFree) allergensArr.push(Allergen.DAIRY_FREE)
    if (isGlutenFree) allergensArr.push(Allergen.GLUTEN_FREE)
    if (isVegan) allergensArr.push(Allergen.VEGAN)
    if (isVegetarian) allergensArr.push(Allergen.VEGETARIAN)
    if (isKeto) allergensArr.push(Allergen.KETO)
    return allergensArr
  }

  const itemImage = imageJitURL(image, {
    resize: {
      width: 128,
      height: 128,
      fit: 'cover',
    },
  })

  const basketItemId = addOnItems.find(
    item => item.menuItemId === menuItemId
  )?.id

  const canAddMore =
    !groupIsAtMaxQuantity &&
    (maxPurchaseQuantity === null ||
      quantityOfSelectedItem < maxPurchaseQuantity)

  const disabledState = quantityOfSelectedItem !== 0 ? false : !canAddMore
  const strikethroughPrice = price ?? addOnMaxPrice
  return (
    <>
      {/* TODO - This would ideally be based in the PageWrapper component next to DirtyBasketAlert */}
      {/* Trouble is the hook hell that is required to get this to work, otherwise we'd cater for more edge cases with these add ons */}
      <AlertModal
        isOpen={showConflictModal}
        title={t('alert_replace_items_title')}
        subTitle={t('alert_replace_items_desc', { conflictingGroup })}
        action={{
          text: t('alert_replace_items_confirm'),
          intent: ButtonType.PRIMARY,
          onClick: handleConflictConfirm,
        }}
        cancel={{
          text: t('alert_replace_items_cancel'),
          intent: ButtonType.SECONDARY,
          onClick: () => setShowConflictModal(false),
        }}
        dataTestId="outlet-addonitem-modal-replaceconflict"
      />
      <AddOnItemContainer
        key={outletMenuItemId}
        noHover={quantityOfSelectedItem === 0}
        isDisabled={!canAddMore}
        onClick={() => {
          if (!canAddMore) return
          if (quantityOfSelectedItem === 0)
            selectAddOnItem({
              menuItemId,
              price: discountPrice,
              quantity: 1,
              outletMenuItemId,
              name,
              optionItemIds: [],
              singleItemNotes: '',
              addOnItem: true,
            })
        }}
        data-test-id={`outlet-addonitem-container-card-${outletMenuItemId}`}
      >
        <AddOnDetailsColumn>
          <HeaderHolder>
            <ItemDetailsHeader data-test-id="outlet-addonitem-text-name">
              {name}
            </ItemDetailsHeader>
            <ChilliContainer>
              {addOnItem.spiceLevel > 0 &&
                Array.from({ length: addOnItem.spiceLevel }).map((_, index) => (
                  <SpicyChiliSVG
                    width={15}
                    height={15}
                    key={index}
                    data-test-id="outlet-addonitem-icon-spice"
                  />
                ))}
            </ChilliContainer>
          </HeaderHolder>
          <AllergenTagHolder data-test-id="outlet-addonitem-container-allergens">
            <AllergenTags
              allergens={presentAllergens()}
              iconView
              ageRestricted={!!ageRestricted}
            />
          </AllergenTagHolder>
          {description && (
            <MenuDescription data-test-id="outlet-addonitem-text-description">
              {description}
            </MenuDescription>
          )}
          <AddOnItemDetails
            selectedAnItem={quantityOfSelectedItem > 0}
            data-test-id="outlet-addonitem-container-details"
          >
            <StyledBasketBoostSVG fill={theme.colors.brand} id="add-on-items" />
            <AddOnPriceContainer data-test-id="outlet-addonitem-container-price">
              <AddOnItemRegularPrice data-test-id="outlet-addonitem-text-regularprice">
                {strikethroughPrice ? formatCurrency(strikethroughPrice) : null}
              </AddOnItemRegularPrice>
              <AddOnItemPrice data-test-id="outlet-addonitem-text-discountprice">
                {discountPrice > 0 ? formatCurrency(discountPrice) : t('free')}
              </AddOnItemPrice>
            </AddOnPriceContainer>
            <ItemCounter
              allowAddItem={canAddMore}
              isDisabled={disabledState}
              addItemText={
                maxPurchaseQuantity !== null &&
                quantityOfSelectedItem >= maxPurchaseQuantity
                  ? t('maximum_addon_purchase_quantity')
                  : t('add_offer')
              }
              allowZeroCount={false}
              oldPrice={price ?? undefined}
              hidePrice
              count={quantityOfSelectedItem}
              isMenuItem
              price={price}
              handleAdd={() => {
                if (quantityOfSelectedItem > 0)
                  selectAddOnItem({
                    menuItemId,
                    price: discountPrice,
                    quantity: 1,
                    outletMenuItemId,
                    name,
                    optionItemIds: [],
                    singleItemNotes: '',
                    addOnItem: true,
                  })
              }}
              handleRemove={() => {
                if (basketItemId) {
                  if (quantityOfSelectedItem === 1) {
                    removeItem({
                      basketItemId,
                      shouldNavigateToBasket: false,
                    })
                  } else {
                    updateQuantity({
                      basketItemId,
                      quantity: quantityOfSelectedItem - 1,
                      shouldNavigateToBasket: false,
                    })
                  }
                }
              }}
              data-test-id="outlet-addonitem-counter-quantity"
            />
          </AddOnItemDetails>
        </AddOnDetailsColumn>
        {showItemThumbnail && (
          <ImageContainer>
            <ItemImage
              role="img"
              aria-label={name}
              imageURL={itemImage}
              isDisabled={disabledState}
              data-test-id="outlet-addonitem-image-thumbnail"
            />
            <DetailsContainer />
          </ImageContainer>
        )}
      </AddOnItemContainer>
    </>
  )
}
